import React, { useCallback, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getPartnerCompanyEvent, getPartnerCompanyEventTasksByEvent } from 'src/api/partner';
import TasksTab from 'src/components/partner-portal/TasksTab';
import { Spinner, TaskDialog } from '@repo/components';
import { Alert } from '@repo/components';
import { Task } from '@repo/shared/domain';
import { constructQuery } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/events/$eventId/tasks/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		filter: (search.filter as string) || 'exclude_superseded',
		page: search.page !== undefined ? Number(search.page) : 0,
		sort: (search.sort as string) || 'startTime:desc',
	}),
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId, eventId } = Route.useParams();
	useBreadcrumbLevel(3, 'Tasks');
	const { filter, page, sort } = Route.useSearch();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [selectedTask, setSelectedTask] = useState<Task | null>(null);

	// Get event data
	const {
		data: event,
		isLoading: eventLoading,
		error: eventError,
	} = useQuery({
		queryKey: ['partner-company-event', partnerId, eventId],
		queryFn: () => getPartnerCompanyEvent(partnerId, eventId),
		enabled: !userLoading && !!authenticatedUser,
	});

	// Get event tasks
	const { data: tasks, isLoading: tasksLoading } = useQuery({
		queryKey: ['partner-company-event-tasks', partnerId, eventId, { filter, page, sort }],
		queryFn: () =>
			getPartnerCompanyEventTasksByEvent(partnerId, eventId, constructQuery({ filter, page: page.toString(), sort })),
		enabled: !userLoading && !!authenticatedUser && !!event,
	});

	const handleReload = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['partner-company-event-tasks', partnerId, eventId] });
	}, [queryClient, partnerId, eventId]);

	const handleUpdate = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['partner-company-event', partnerId, eventId] });
		queryClient.invalidateQueries({ queryKey: ['partner-company-event-tasks', partnerId, eventId] });
	}, [queryClient, partnerId, eventId]);

	const handleTaskSelected = useCallback(
		(taskId: string) => {
			const task = tasks?.data?.find((t) => t.id === taskId);
			if (!task) return;
			if (task.status === 'todo') {
				setSelectedTask(task);
			} else {
				navigate({ to: `/partner/${partnerId}/events/${eventId}/tasks/${taskId}` });
			}
		},
		[navigate, partnerId, eventId, tasks],
	);

	const handleTaskDialogClose = useCallback(() => {
		setSelectedTask(null);
		handleReload();
	}, [handleReload]);

	if (userLoading || eventLoading || tasksLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (eventError || !event) {
		return (
			<div className="p-6">
				<Alert variant="destructive">Event not found</Alert>
			</div>
		);
	}

	if (!authenticatedUser) return null;

	return (
		<div className="p-6">
			<TasksTab
				tasks={tasks}
				authenticatedUser={authenticatedUser}
				onUpdate={handleUpdate}
				onReload={handleReload}
				onTaskSelected={handleTaskSelected}
				filter={filter}
				sort={sort}
			/>
			<TaskDialog
				task={selectedTask}
				onClose={handleTaskDialogClose}
				auth=""
				userId={authenticatedUser?.id || selectedTask?.userId}
				hideAppBar={false}
				showTitlePage={false}
				lng=""
			/>
		</div>
	);
}
